import { useState, useEffect } from 'react';
import { tasksAPI } from '../api/tasks.api';
import { useSocket } from '@/contexts/SocketContext';

export const useUnreadCount = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { socket, isConnected } = useSocket();

  const fetchUnreadCount = async () => {
    try {
      setIsLoading(true);
      const response = await tasksAPI.getUnreadCount();
      if (response.success) {
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    
    // Only poll if WebSocket is not connected (fallback mode)
    // When WebSocket is connected, we'll get real-time updates
    if (!isConnected) {
      // Poll for updates every 30 seconds as fallback (increased from 5s)
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isConnected]);

  // Listen for WebSocket events that should update the unread count
  useEffect(() => {
    if (!socket) return;

    const handleTaskCreated = () => {
      // Refetch unread count when a new task is created
      fetchUnreadCount();
    };

    const handleTaskViewed = () => {
      // Refetch when a task is marked as viewed
      fetchUnreadCount();
    };

    const handleCommentAdded = () => {
      // Refetch when a comment is added
      fetchUnreadCount();
    };

    // Listen to relevant WebSocket events
    socket.on('task:created', handleTaskCreated);
    socket.on('task:viewed', handleTaskViewed);
    socket.on('task:updated', handleTaskCreated); // Task updates might change unread status
    socket.on('comment:added', handleCommentAdded);
    socket.on('task:comment:notification', handleCommentAdded);
    socket.on('chat:message', handleCommentAdded); // Chat messages should update badge too

    return () => {
      socket.off('task:created', handleTaskCreated);
      socket.off('task:viewed', handleTaskViewed);
      socket.off('task:updated', handleTaskCreated);
      socket.off('comment:added', handleCommentAdded);
      socket.off('task:comment:notification', handleCommentAdded);
      socket.off('chat:message', handleCommentAdded);
    };
  }, [socket]);

  return {
    unreadCount,
    isLoading,
    refetch: fetchUnreadCount
  };
};